import React, { useState, useContext, useMemo } from 'react'
import { Row, Col, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap'
import styled, { css } from 'styled-components'
import {
  FaStar,
  FaGraduationCap,
  FaChessKnight,
  FaInfoCircle,
  FaGlobe,
  FaSnowflake,
  FaIdCard,
  FaUniversalAccess,
  FaChalkboardTeacher,
  FaDiceD20,
  FaUniversity,
  FaMoneyBillWave,
  FaUserTie,
  FaCubes,
  FaFighterJet,
  FaUsers,
  FaRunning,
  FaSpa,
  FaUserMd,
  FaEllipsisH,
  FaUserFriends,
  FaSchool,
  FaUserGraduate,
  FaCalendarAlt,
  FaHotjar,
  FaEnvelopeOpenText,
} from 'react-icons/fa'
import snappIcon from 'images/snapp-icon.png'
import CurrencyFormat from 'react-currency-format'
import nspaIcon from 'images/nspa-icon.png'
import { device } from 'utils/device'
import { camelCase, startCase } from 'lodash'
import moment from 'moment'
import { I18nContext } from 'i18n'
import Markdown from 'react-markdown'

const Program = ({ program, onClick }) => {
  const {
    programName,
    blurb,
    logoURL,
    openDate,
    closeDate,
    criteriaDescription,
    scholarshipMaximumAward,
    criterias,
    eligibilityCriteriaDescription,
    isNspaMember,
  } = program

  const criteriaDesc = criteriaDescription || eligibilityCriteriaDescription
  const { translate } = useContext(I18nContext)
  const description = `${blurb || ''} \n\n ${criteriaDesc}`
  const canTruncate = criteriaDesc?.length > 100
  const [truncated, setTruncated] = useState(canTruncate)
  const icon = logoURL || snappIcon

  const truncate = (str) => {
    if (str) {
      //check by blur first
      if (blurb?.length > 300)
        return <Markdown source={`${str.substring(0, 300)}...`} />

      if (criteriaDesc) {
        return (
          <Markdown
            source={`${blurb || ''} \n\n **${translate(
              'eligibilityCriteria'
            )}**... `}
          />
        )
      }

      return <Markdown source={str} />
    }
  }

  const renderFullDesc = () => {
    return (
      <>
        <Markdown source={blurb || ''} />
        <Detail>
          {(canTruncate || criteriaDesc) && translate('program_criteria_desc')}
        </Detail>
        <List>
          <Markdown source={criteriaDesc || ''} />
        </List>
      </>
    )
  }

  const icons = useMemo(
    () => ({
      specialCriteria: <FaChessKnight />,
      academics: <FaStar />,
      seekingDegree: <FaGraduationCap />,
      degreeSeeking: <FaGraduationCap />,
      locations: <FaGlobe />,
      awardUseLocations: <FaGlobe />,
      demographics: <FaSnowflake />,
      citizenship: <FaIdCard />,
      citizenshipStatuses: <FaIdCard />,
      collegeReadinessProgramParticipation: <FaUniversalAccess />,
      currentGradeLevel: <FaChalkboardTeacher />,
      graduationStatus: <FaDiceD20 />,
      graduation: <FaDiceD20 />,
      collegeAttendance: <FaUniversity />,
      financialInformation: <FaMoneyBillWave />,
      profession: <FaUserTie />,
      fieldsOfStudy: <FaCubes />,
      armedServices: <FaFighterJet />,
      familyArmedServiceHistory: <FaUsers />,
      activity: <FaRunning />,
      interests: <FaChessKnight />,
      affiliations: <FaSpa />,
      condition: <FaUserMd />,
      miscellaneous: <FaEllipsisH />,
      situation: <FaUserFriends />,
      enrollmentStatus: <FaUserFriends />,
      applicationRestrictions: <FaSchool />,
      collegePlan: <FaSchool />,
      currentEnrollment: <FaUserGraduate />,
      currentSchool: <FaUserGraduate />,
      age: <FaCalendarAlt />,
      unforeseenEvent: <FaHotjar />,
      affiliation: <FaEnvelopeOpenText />,
      parentProfession: <FaUserTie />,
      applicationRestriction: <FaSchool />,
    }),
    []
  )

  return (
    <ProgramContainer>
      <Row>
        <Header onClick={() => onClick(program.id, isNspaMember)}>
          {programName}
        </Header>
      </Row>
      <LogoDesc>
        <Col md={2} className="justify-content-center">
          <Image
            src={icon}
            alt=""
            onClick={() => onClick(program.id, isNspaMember)}
          />
        </Col>
        <Col>
          <Eligibility>
            {truncated ? truncate(description) : renderFullDesc()}
            {canTruncate && (
              <More variant="primary" onClick={() => setTruncated(!truncated)}>
                {truncated
                  ? translate('program_more')
                  : translate('program_less')}
              </More>
            )}
          </Eligibility>
        </Col>
      </LogoDesc>
      <DateContainer>
        <Col>
          <span>{translate('program_list_open')}:</span>{' '}
          {openDate ? moment(openDate).format('MMM. D, YYYY') : null}
        </Col>
        <Col md="6" xs="6">
          <span>{translate('program_deadline')}:</span>{' '}
          {closeDate ? moment(closeDate).format('MMM. D, YYYY') : null}
        </Col>
        <Col>
          {scholarshipMaximumAward > 0 && (
            <CurrencyFormat
              value={scholarshipMaximumAward || 0}
              displayType={'text'}
              thousandSeparator={true}
              prefix={'$'}
            />
          )}
        </Col>
      </DateContainer>
      <FactorContainer>
        <Col xs="12" md="6" className="text-left">
          {translate('program_factors')}: <i className="fa fa-ad"></i>
          {criterias &&
            Object.keys(criterias).map((item, ix) => (
              <OverlayTrigger
                key={ix}
                placement="bottom"
                overlay={
                  <Tooltip>
                    {startCase(item.replace('DegreeSeeking', 'SeekingDegree'))}
                  </Tooltip>
                }
              >
                <CriteriaIcon>{icons[camelCase(item)]}</CriteriaIcon>
              </OverlayTrigger>
            ))}
        </Col>
        {isNspaMember && (
          <Col xs="12" md="6">
            <NspaIcon src={nspaIcon} alt="" />
            {translate('program_nspa')}
          </Col>
        )}
      </FactorContainer>
    </ProgramContainer>
  )
}

const iconBlue = css`
  color: ${({ theme }) => theme.palette.primary[1]};
  font-size: 0.9rem;
  margin-right: 0.3rem;
`

const CriteriaIcon = styled.i`
  ${iconBlue}
`

const ProgramContainer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.palette.grayscale[1]};
  padding: 0.2rem;
  color: ${({ theme }) => theme.palette.grayscale[1]};
  font-size: 0.9rem;
  font-weight: ${({ theme }) => theme.fonts.weight.light};
  margin-bottom: 1.5rem;

  @media ${device.laptopL} {
    width: auto;
  }
`

const Header = styled(Col)`
  padding: 0.2rem 1.5rem;
  font-size: 1rem;
  cursor: pointer;
  font-weight: ${({ theme }) => theme.fonts.weight.normal};

  &:hover {
    text-decoration: underline;
  }
`

const LogoDesc = styled(Row)`
  border-bottom: 1px solid ${({ theme }) => theme.palette.grayscale[6]};
  border-top: 1px solid ${({ theme }) => theme.palette.grayscale[6]};
  margin: 0 -0.2rem;
  padding: 0.5rem 0.8rem;

  @media ${device.mobileL} {
    padding: 0;
  }
`

const DateContainer = styled(Row)`
  margin: 0.5rem 0;
  white-space: nowrap;

  > div {
    padding-left: 0.5rem;

    > span {
      font-weight: ${({ theme }) => theme.fonts.weight.bold};
    }

    &:last-child {
      text-align: right;
      font-weight: ${({ theme }) => theme.fonts.weight.bold};
    }
  }
`

const FactorContainer = styled(Row)`
  border-top: 1px solid ${({ theme }) => theme.palette.grayscale[6]};
  margin: 0 -0.2rem;
  padding: 0.5rem 0rem;

  > div {
    padding-left: 0.5rem;

    &:last-child {
      text-align: right;

      @media ${device.mobileL} {
        text-align: left;
        margin-top: 0.5rem;
      }
    }
  }
`

const More = styled(Badge)`
  background-color: ${({ theme }) => theme.palette.primary[2]};
  cursor: pointer;
  margin-left: 0.5rem;
  float: right;
`

const Image = styled.img`
  max-width: 70px;
  cursor: pointer;

  @media ${device.mobileL} {
    margin: 1rem;
    max-width: 70%;
    max-height: 8rem;
  }
`

const NspaIcon = styled.img`
  width: 24px;
  margin-right: 0.5rem;
`

const Detail = styled.div`
  margin-top: 1rem;
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
`

const Eligibility = styled.div`
  white-space: pre-wrap;
  strong {
    font-weight: 600;
  }
`

const List = styled.div`
  > ul {
    list-style: square;
    padding-left: 1rem;
    padding-top: 1rem;
  }
`

export default React.memo(Program)
